import React, { useState, useEffect, useCallback, useContext } from 'react';
import { AuthContext } from '../App';
import { useNavigate } from 'react-router-dom';
import { 
  AlertTriangle, Filter, ChevronLeft, ChevronRight, X, 
  Clock, User, MessageSquare, CheckCircle, AlertCircle
} from 'lucide-react';
import AppHeader from '../components/AppHeader';

const useAuth = () => useContext(AuthContext);

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SEVERITY_COLORS = {
  low: 'bg-green-100 text-green-800',
  moderate: 'bg-yellow-100 text-yellow-800',
  high: 'bg-red-100 text-red-800'
};

const STATUS_COLORS = {
  open: 'bg-red-100 text-red-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  closed: 'bg-green-100 text-green-800'
};

export default function EscalationsPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [escalations, setEscalations] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [page, setPage] = useState(0);
  const limit = 20;
  
  // Detail modal
  const [selectedEscalation, setSelectedEscalation] = useState(null);
  const [editNotes, setEditNotes] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [saving, setSaving] = useState(false);

  // Check access
  const hasAccess = user?.sub_role === 'supervisor' || 
                    user?.sub_role === 'student_welfare_head' || 
                    user?.role === 'admin' ||
                    user?.base_role === 'admin';

  const fetchEscalations = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: page * limit,
        limit: limit
      });
      if (statusFilter) params.append('status', statusFilter);
      if (severityFilter) params.append('severity', severityFilter);
      
      const res = await fetch(`${API_URL}/api/mvp/escalations?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setEscalations(data.escalations || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to fetch escalations:', err);
    } finally {
      setLoading(false);
    }
  }, [token, page, statusFilter, severityFilter]);

  useEffect(() => {
    if (!hasAccess) {
      navigate('/');
      return;
    }
    fetchEscalations();
  }, [hasAccess, navigate, fetchEscalations]);

  const openDetail = async (escalation) => {
    try {
      const res = await fetch(`${API_URL}/api/mvp/escalations/${escalation.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setSelectedEscalation(data);
      setEditNotes(data.notes || '');
      setEditStatus(data.status);
    } catch (err) {
      console.error('Failed to fetch escalation detail:', err);
    }
  };

  const handleUpdateEscalation = async () => {
    if (!selectedEscalation) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/mvp/escalations/${selectedEscalation.id}?updated_by_user_id=${user.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          status: editStatus,
          notes: editNotes
        })
      });
      if (!res.ok) throw new Error('Failed to update');
      setSelectedEscalation(null);
      fetchEscalations();
    } catch (err) {
      alert('Failed to update escalation');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  const totalPages = Math.ceil(total / limit);

  if (!hasAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <AlertCircle className="mx-auto text-red-500 mb-4" size={48} />
          <h1 className="text-xl font-semibold text-gray-800">Access Denied</h1>
          <p className="text-gray-600 mt-2">You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader title="Escalations" />
      <div className="max-w-7xl mx-auto p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <AlertTriangle className="text-orange-600" size={28} />
          <h1 className="text-2xl font-bold text-gray-800">Escalations</h1>
          <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
            {total} total
          </span>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            <Filter className="text-gray-400" size={20} />
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
            >
              <option value="">All Status</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="closed">Closed</option>
            </select>
            <select
              value={severityFilter}
              onChange={(e) => { setSeverityFilter(e.target.value); setPage(0); }}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
            >
              <option value="">All Severity</option>
              <option value="low">Low</option>
              <option value="moderate">Moderate</option>
              <option value="high">High</option>
            </select>
          </div>
        </div>

        {/* Escalations List */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : escalations.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <CheckCircle className="mx-auto text-green-500 mb-2" size={40} />
              <p>No escalations found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Masked ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Initiated By</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Severity</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Summary</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {escalations.map(esc => (
                    <tr 
                      key={esc.id} 
                      onClick={() => openDetail(esc)}
                      className="hover:bg-orange-50 cursor-pointer"
                    >
                      <td className="px-4 py-3 text-sm font-mono text-gray-900">{esc.student_masked_id && esc.student_masked_id !== 'undefined' && esc.student_masked_id !== 'null' ? esc.student_masked_id : 'ID unavailable'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{esc.initiated_by_name}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded-full font-medium ${SEVERITY_COLORS[esc.severity]}`}>
                          {esc.severity}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded-full font-medium ${STATUS_COLORS[esc.status]}`}>
                          {esc.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{formatDate(esc.created_at)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">{esc.summary}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-4 py-3 border-t flex items-center justify-between">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="flex items-center gap-1 px-3 py-1 text-sm border rounded disabled:opacity-50"
              >
                <ChevronLeft size={16} /> Prev
              </button>
              <span className="text-sm text-gray-600">
                Page {page + 1} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="flex items-center gap-1 px-3 py-1 text-sm border rounded disabled:opacity-50"
              >
                Next <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>

        {/* Detail Modal */}
        {selectedEscalation && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <AlertTriangle className="text-orange-600" size={20} />
                  Escalation Detail
                </h2>
                <button onClick={() => setSelectedEscalation(null)}><X size={20} /></button>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-xs text-gray-500 uppercase mb-1">Care-Seeker Masked ID</label>
                  <p className="font-mono text-gray-900">{selectedEscalation.student_masked_id && selectedEscalation.student_masked_id !== 'undefined' && selectedEscalation.student_masked_id !== 'null' ? selectedEscalation.student_masked_id : 'ID unavailable'}</p>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 uppercase mb-1">Initiated By</label>
                  <p className="text-gray-900">{selectedEscalation.initiated_by?.name || '-'}</p>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 uppercase mb-1">Created</label>
                  <p className="text-gray-900">{formatDate(selectedEscalation.created_at)}</p>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 uppercase mb-1">Assigned To</label>
                  <p className="text-gray-900 capitalize">{selectedEscalation.assigned_to_role}</p>
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-xs text-gray-500 uppercase mb-1">Summary</label>
                <p className="p-3 bg-gray-50 rounded-lg text-gray-900">{selectedEscalation.summary}</p>
              </div>
              
              {selectedEscalation.session && (
                <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                  <label className="block text-xs text-blue-600 uppercase mb-1">Linked Session</label>
                  <p className="text-sm text-blue-800">
                    Session ID: {selectedEscalation.session.id} | 
                    Type: {selectedEscalation.session.session_type || selectedEscalation.session.grade_code}
                  </p>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs text-gray-500 uppercase mb-1">Severity</label>
                  <span className={`px-3 py-1 text-sm rounded-full font-medium ${SEVERITY_COLORS[selectedEscalation.severity]}`}>
                    {selectedEscalation.severity}
                  </span>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 uppercase mb-1">Status</label>
                  <select
                    value={editStatus}
                    onChange={(e) => setEditStatus(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
              </div>
              
              <div className="mb-6">
                <label className="block text-xs text-gray-500 uppercase mb-1">Notes</label>
                <textarea
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Add notes about this escalation..."
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setSelectedEscalation(null)}
                  className="flex-1 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateEscalation}
                  disabled={saving}
                  className="flex-1 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
